import { type PocketbaseError } from "#api/types";
import { get } from "./manipulators";

// Default error that will be used when no error message is found
const DEFAULT_ERROR_MESSAGE = "An unknown error occurred";

/**
 * Assert that an unknown error derives from 'Error' (changes type after function call)
 *
 * Use to ensure that 'unknown' errors from try/catch blocks are actually derived from 'Error' class,
 *   to prevent invalidly accessing standard error properties (ie. 'message'). Invalid errors will
 *   be thrown as-is, and may not be caught by anything!
 *
 * @param  error - Unknown error shape
 * @throws Original error if not derived from 'Error' class
 *
 * @example
 * try {
 *   ...
 * } catch (e: unknown) {
 *   assertError(e);  // Will assert that 'e' is an 'Error' or throw otherwise
 *   console.log(e.message);
 * }
 */
// NOTE: Strange type syntax is necessary to fix 'Assertions require every name in the call...' error
//         when using "standard" TS assertion typing (ie. on right side of function vs left).
// Source: https://github.com/microsoft/TypeScript/pull/33622#issuecomment-575301357
export const assertError: (error: unknown) => asserts error is Error = (error: unknown) => {
  if (!(error instanceof Error)) {
    // TODO: Determine whether this actually makes sense, as it will effectively throw errors within 'catch'
    //         clauses which will not be handled in the same way as the server!
    throw error;
  }
};

/**
 * Throws an error if the given condition is "false", and asserts that the condition is "true.""
 *
 * NOTE: Intended for use with "simple" conditions (ie. single condition, verifying something exists, etc),
 *         as using everywhere can reduce readability.
 *
 * @param condition - Evaluation condition (throws specified error if evaluating to false)
 * @param error     - Error/message thrown if condition if false (uses generic error message if skipped)
 * @throws Provided error (or a generic error) if the condition is false
 */
// NOTE: Must use 'function' signature due to funky TS/JS issues with arrow function assertions 🤷
export function assertCondition(condition: unknown, error?: Error | string): asserts condition {
  if (!condition) {
    if (!error) {
      throw new Error("Assertion failed");
    } else if (typeof error === "string") {
      throw new Error(error);
    } else {
      throw error;
    }
  }
}

/** Normalize Pocketbase error key paths to handle multiple input formats/prefixes */
const normalizePocketbaseErrorPath = (keyPath: string): string => {
  if (keyPath.startsWith("data.") && !keyPath.startsWith("data.data.")) {
    return `data.${keyPath}`;
  } else if (!keyPath.startsWith("data")) {
    return `data.data.${keyPath}`;
  }
  return keyPath;
};

/**
 * Determine whether an error includes a specific error status code number
 *
 * @param   error           - Error object/string
 * @param   targetErrorCode - Target error status code
 */
export const hasStatusCode = (error: unknown, targetErrorCode: number): boolean => {
  if (!isPocketbaseError(error)) return false;

  return error.status === targetErrorCode;
};

/**
 * Get an error message from an error
 *
 * @param   error          - Error object/string
 * @param   defaultMessage - Default error message
 */
export const getError = (error: unknown, defaultMessage?: string): string => {
  if (!error) return "";

  const errorMessage = getErrorMessage(error);
  if (!errorMessage) {
    return defaultMessage ?? DEFAULT_ERROR_MESSAGE;
  }

  return errorMessage;
};

/**
 * Get a nested error message from a Pocketbase error
 *
 * NOTE: Will return standard top-level `message` field if nested error message is not found.
 *
 * @param keyPath        - Path to nested error message in Pocketbase error `data.data[xxx]` object
 * @param defaultMessage - Default error message
 */
export const getPocketbaseError = (
  error: unknown,
  keyPath: string,
  defaultMessage?: string,
): string => {
  if (!error) return "";

  if (!isPocketbaseError(error)) {
    return getError(error, defaultMessage);
  }

  keyPath = normalizePocketbaseErrorPath(keyPath);
  return get(error, keyPath) ?? getError(error, defaultMessage);
};

/**
 * Get an error message from an error
 *
 * NOTE: Pocketbase errors generally have a top-level 'message' field with a semi-generic message, and a more
 *         detailed message in the nested 'data' object (use `getPocketbaseError`).
 *
 * @param   error - Error object/string
 */
const getErrorMessage = (error: unknown): string | null => {
  if (!error) return null;

  // A bare error code may be provided instead of an error object
  if (typeof error === "string") return error;

  if (isPocketbaseError(error)) {
    return getErrorMessage(error.response);
  }

  // Errors are often provided as an object, but the message 'key' may vary
  if (typeof error === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorObject = error as any;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const message = errorObject.message || errorObject.error;

    return getErrorMessage(message);
  }

  return null;
};

/**
 * Get a specific Pocketbase error code from an error
 *
 * @param keyPath - Path to nested error code in Pocketbase error `data.data[xxx]` object
 */
export const getPocketbaseCode = (error: unknown, keyPath: string): string | undefined => {
  if (!isPocketbaseError(error)) return undefined;

  keyPath = normalizePocketbaseErrorPath(keyPath);
  return get(error, keyPath);
};

/**
 * Check if a Pocketbase error includes a specific error code
 *
 * @param keyPath    - Path to nested error code in Pocketbase error `data.data[xxx]` object
 * @param targetCode - Target error code
 */
export const hasPocketbaseCode = (error: unknown, keyPath: string, targetCode: string): boolean => {
  const code = getPocketbaseCode(error, keyPath);
  return code === targetCode;
};

/** Whether an error is a Pocketbase error */
export const isPocketbaseError = (error: unknown): error is PocketbaseError => {
  if (typeof error !== "object" || error === null) return false;

  const keys = ["originalError", "response", "status", "url"];
  const errorKeys = Object.keys(error);
  return keys.every((key) => errorKeys.includes(key));
};
